import { Alert, Button, Flex, Typography } from "antd"
import React, { useCallback, useEffect, useRef, useState } from "react"
import { useTranslation } from "react-i18next"

import { useIdempotencyKey } from "../../../hooks/use-idempotency-key.ts"
import { TextArea } from "../../../components/Input/TextArea.tsx"
import { cn } from "../../../utils/cn.ts"
import {
  CUSTOM_FOCUS_CODE,
  FOCUS_CODES,
  suggestionFocusCodes
} from "../../Lead/LeadWorkspacePage/education/education-contract.ts"
import { classifyMoreOutcome } from "../api/public-more-outcome.ts"
import {
  fetchEducationMoreStatus,
  requestEducationMore
} from "../api/public-education.services"

const { Text } = Typography

// The customer's focus codes minus "custom", which is its own "Ask a
// question" chip that reveals the text field.
const CHIP_CODES = FOCUS_CODES.customer.filter((code) => code !== CUSTOM_FOCUS_CODE)
const CUSTOM_CODE = CUSTOM_FOCUS_CODE

const MIN_QUESTION_LENGTH = 3
const MAX_QUESTION_LENGTH = 300
const POLL_INTERVAL_MS = 3_000

type PanelState = "idle" | "submitting" | "polling" | "review"

interface RejectionState {
  message: string
  suggestions: string[]
}

interface CustomerMorePanelProps {
  session: string
  remaining: number
  /** Called once a request settles into content the guide should show:
   *  `publicId` given means "ready — refetch and select this edition",
   *  omitted means "review — just refetch so the remaining count is current." */
  onGuideShouldRefresh: (publicId?: string) => void
}

/**
 * Screen 9's "Want more detail?" — the customer-facing counterpart to the
 * internal `AskForMoreModal`, inline in the page rather than a modal, with a
 * fixed three-chip topic list (no lead-updates checkbox, no sales-person
 * focus codes: `request_more` asserts `track.audience == customer` for a
 * customer-created request, so this panel never needs to know about the
 * sales brief at all).
 */
export function CustomerMorePanel({
  session,
  remaining,
  onGuideShouldRefresh
}: CustomerMorePanelProps) {
  const { t } = useTranslation()
  const { key, reset } = useIdempotencyKey()
  const [selected, setSelected] = useState<string | null>(null)
  const [question, setQuestion] = useState("")
  const [state, setState] = useState<PanelState>("idle")
  const [limitReached, setLimitReached] = useState(false)
  const [rejection, setRejection] = useState<RejectionState | null>(null)
  const [genericError, setGenericError] = useState<string | null>(null)

  const pollTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const attemptIdRef = useRef(0)

  const clearPoll = useCallback(() => {
    if (pollTimerRef.current) {
      clearTimeout(pollTimerRef.current)
      pollTimerRef.current = null
    }
  }, [])

  useEffect(
    () => () => {
      clearPoll()
      attemptIdRef.current++
    },
    [clearPoll]
  )

  const isCustom = selected === CUSTOM_CODE
  const trimmedQuestion = question.trim()
  const questionValid =
    trimmedQuestion.length >= MIN_QUESTION_LENGTH && trimmedQuestion.length <= MAX_QUESTION_LENGTH
  const canSubmit =
    remaining > 0 &&
    state !== "submitting" &&
    state !== "polling" &&
    !!selected &&
    (!isCustom || questionValid)

  const resetOutcomes = () => {
    setLimitReached(false)
    setRejection(null)
    setGenericError(null)
  }

  const pollStatus = useCallback(
    (publicId: string) => {
      clearPoll()
      const attemptId = ++attemptIdRef.current
      const poll = () => {
        void fetchEducationMoreStatus(session, publicId).then((response) => {
          if (attemptId !== attemptIdRef.current) return
          const status = response.data?.status
          if (status === "ready") {
            setState("idle")
            setSelected(null)
            setQuestion("")
            reset()
            onGuideShouldRefresh(publicId)
            return
          }
          if (status === "review") {
            setState("review")
            reset()
            onGuideShouldRefresh()
            return
          }
          if (status === "pending" && response.ok) {
            pollTimerRef.current = setTimeout(poll, POLL_INTERVAL_MS)
            return
          }
          // "unavailable", a dropped response, or the request itself failing:
          // this one ask stalled, but the guide the customer is already
          // reading is still fine — never escalate to the page-level
          // unavailable state over a single More request.
          setState("idle")
          setGenericError(t("projectEducation.publicPage.moreDetail.genericError"))
        })
      }
      poll()
    },
    [session, clearPoll, onGuideShouldRefresh, reset, t]
  )

  const handleSubmit = async () => {
    if (!canSubmit) return
    setState("submitting")
    resetOutcomes()

    const body = isCustom ? { focus_code: selected, question: trimmedQuestion } : { focus_code: selected! }
    const response = await requestEducationMore(session, key, body)

    if (response.ok && response.data) {
      setState("polling")
      pollStatus(response.data.public_id)
      return
    }

    setState("idle")
    const outcome = classifyMoreOutcome(response.error?.code, response.status)
    if (outcome === "limit") {
      setLimitReached(true)
      reset()
      return
    }
    if (outcome === "rejected") {
      setRejection({
        // The backend sends one fixed English sentence for every rejection.
        message: t("projectEducation.publicPage.moreDetail.outsideScope"),
        suggestions: suggestionFocusCodes("customer", response.error?.suggestions ?? []).filter(
          (code) => code !== CUSTOM_CODE
        )
      })
      reset()
      return
    }
    if (outcome === "unavailable") {
      // The link went away mid-visit: the page's refetch answers 404 and
      // shows the unavailable screen.
      reset()
      onGuideShouldRefresh()
      return
    }
    setGenericError(t("projectEducation.publicPage.moreDetail.genericError"))
    // A true network failure keeps the same idempotency key so a retry lands
    // as the same attempt; anything else is a settled outcome.
    if (response.status !== 0) reset()
  }

  const chipLabel = (code: string) => t(`projectEducation.publicPage.moreDetail.chips.${code}`)

  const renderChip = (
    code: string,
    label: string,
    testId?: string
  ) => {
    const pressed = selected === code
    return (
      <button
        key={code}
        type={"button"}
        aria-pressed={pressed}
        data-testid={testId}
        onClick={() => {
          setSelected(pressed ? null : code)
          resetOutcomes()
        }}
        className={cn(
          "rounded-lg border px-3 py-1 text-sm transition-colors",
          pressed
            ? "border-[#2A7D8C] bg-[#2A7D8C]/10 text-[#2A7D8C]"
            : "border-gray-300 bg-white text-gray-800 hover:border-[#2A7D8C]"
        )}
      >
        {label}
      </button>
    )
  }

  const handleTextKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault()
      void handleSubmit()
    }
  }

  return (
    <div data-testid={"project-education-more-panel"} className={"mt-6"}>
      <Text strong className={"block mb-2"}>
        {t("projectEducation.publicPage.moreDetail.heading")}
      </Text>

      <Flex wrap gap={6} className={"mb-3"}>
        {CHIP_CODES.map((code) => renderChip(code, chipLabel(code)))}
        {renderChip(
          CUSTOM_CODE,
          t("projectEducation.publicPage.moreDetail.askQuestion"),
          "project-education-ask-question-chip"
        )}
      </Flex>

      {isCustom && (
        <div className={"mb-3"}>
          <TextArea
            value={question}
            rows={3}
            maxLength={MAX_QUESTION_LENGTH}
            showCount
            placeholder={t("projectEducation.publicPage.moreDetail.questionPlaceholder")}
            onChange={(event) => setQuestion(event.target.value)}
            onKeyDown={handleTextKeyDown}
            status={trimmedQuestion.length > 0 && !questionValid ? "error" : undefined}
            data-testid={"project-education-question-input"}
          />
          {trimmedQuestion.length > 0 && !questionValid && (
            <Text type={"danger"} className={"text-xs"}>
              {t("projectEducation.publicPage.moreDetail.questionTooShort")}
            </Text>
          )}
        </div>
      )}

      <Flex justify={"space-between"} align={"center"} wrap gap={"small"}>
        <Text type={"secondary"} className={"text-xs"}>
          {t("projectEducation.publicPage.moreDetail.requestsAvailable", { count: remaining })}
        </Text>
        <Button
          type={"primary"}
          disabled={!canSubmit}
          loading={state === "submitting"}
          onClick={() => void handleSubmit()}
          data-testid={"project-education-more-submit"}
        >
          {t("projectEducation.publicPage.moreDetail.submit")}
        </Button>
      </Flex>

      {state === "polling" && (
        <Alert
          className={"mt-3"}
          type={"info"}
          showIcon
          message={t("projectEducation.publicPage.moreDetail.pending")}
        />
      )}

      {state === "review" && (
        <Alert
          className={"mt-3"}
          type={"info"}
          showIcon
          message={t("projectEducation.publicPage.moreDetail.review")}
          data-testid={"project-education-more-review"}
        />
      )}

      {limitReached && (
        <Alert
          className={"mt-3"}
          type={"warning"}
          showIcon
          message={t("projectEducation.publicPage.moreDetail.limitReached")}
        />
      )}

      {rejection && (
        <Alert
          className={"mt-3"}
          type={"error"}
          showIcon
          message={t("projectEducation.publicPage.moreDetail.rejected", {
            message: rejection.message
          })}
          description={
            rejection.suggestions.length > 0 ? (
              <Flex vertical gap={4} className={"mt-2"}>
                <Text type={"secondary"} className={"text-xs"}>
                  {t("projectEducation.publicPage.moreDetail.suggestionsLabel")}
                </Text>
                <Flex wrap gap={6}>
                  {rejection.suggestions.map((code) => (
                    <Button
                      key={code}
                      size={"small"}
                      onClick={() => {
                        setSelected(code)
                        setRejection(null)
                      }}
                    >
                      {chipLabel(code)}
                    </Button>
                  ))}
                </Flex>
              </Flex>
            ) : undefined
          }
        />
      )}

      {genericError && <Alert className={"mt-3"} type={"error"} showIcon message={genericError} />}
    </div>
  )
}
